import type { VerortungGeruest, VerortungGeschoss } from '../../types';
import { geschossSchaetzen } from './geschoss';
import type { VerortungsSitzung } from './sitzung';

/**
 * Ebenen der Kamera-Einblendung (docs/verortung-kamera-vertrag.md, Paket A).
 *
 * Eine Ebene ist ein Geschoss des Projekts oder eine Gerüstlage. Beide Listen
 * laufen durch dieselbe Schätzung (`geschossSchaetzen`) — nur die Liste
 * wechselt: im Gerüst-Modus der Sitzung die Lagen, sonst die Geschosse. Die
 * jeweils andere Liste liefert eine Alternative („oder Gerüst 4. Lage“).
 *
 * Reine Funktionen, nichts wirft.
 */

export type EbenenArt = 'geschoss' | 'geruest';

/** Geschoss oder Gerüstlage mit Höhe relativ ±0,00 in m. Passt auf `Geschoss` (geschoss.ts). */
export interface Ebene {
    id: string;
    name: string;
    hoeheM: number;
    art: EbenenArt;
}

/** Grenzen einer gültigen Gerüst-Angabe. */
const MIN_LAGEN = 1;
const MAX_LAGEN = 40;
const MIN_ABSTAND_M = 0.5;
const MAX_ABSTAND_M = 5;
/** Die andere Liste zählt als Alternative bis zu diesem Höhenabstand. */
const ALTERNATIVE_MAX_M = 1.5;

/** Auf mm runden: 0,3 + 2 · 2,0 soll 4,3 zeigen, nicht 4,300000000000001. */
function mm(x: number): number {
    const r = Math.round(x * 1000) / 1000;
    return r === 0 ? 0 : r;
}

/**
 * Lagen „Gerüst 1. Lage“ … „Gerüst n. Lage“, id `geruest-<n>`, Höhe
 * ersteLageM + (n − 1) · abstandM. Leer bei null oder ungültiger Angabe:
 * lagen ganzzahlig 1–40, abstandM 0,5–5 m, ersteLageM endlich.
 */
export function geruestLagen(g: VerortungGeruest | null | undefined): Ebene[] {
    if (!g || typeof g !== 'object') return [];
    const { ersteLageM, abstandM, lagen } = g;
    if (typeof ersteLageM !== 'number' || !Number.isFinite(ersteLageM)) return [];
    if (typeof abstandM !== 'number' || !Number.isFinite(abstandM) || abstandM < MIN_ABSTAND_M || abstandM > MAX_ABSTAND_M) {
        return [];
    }
    if (typeof lagen !== 'number' || !Number.isInteger(lagen) || lagen < MIN_LAGEN || lagen > MAX_LAGEN) return [];
    const ergebnis: Ebene[] = [];
    for (let n = 1; n <= lagen; n++) {
        ergebnis.push({ id: `geruest-${n}`, name: `Gerüst ${n}. Lage`, hoeheM: mm(ersteLageM + (n - 1) * abstandM), art: 'geruest' });
    }
    return ergebnis;
}

/** Geschosse als Ebenen (art 'geschoss'), nach Höhe sortiert; Einträge ohne id/Namen/gültige Höhe fallen weg. */
export function geschossEbenen(geschosse: readonly VerortungGeschoss[]): Ebene[] {
    return (Array.isArray(geschosse) ? geschosse : [])
        .filter(
            (g) =>
                !!g &&
                typeof g.id === 'string' &&
                g.id !== '' &&
                typeof g.name === 'string' &&
                typeof g.hoeheM === 'number' &&
                Number.isFinite(g.hoeheM),
        )
        .map((g): Ebene => ({ id: g.id, name: g.name, hoeheM: g.hoeheM, art: 'geschoss' }))
        .sort((x, y) => x.hoeheM - y.hoeheM);
}

export interface EbenenSchaetzung {
    /** Liste, aus der `ebene` stammt (Sitzung.geruestModus → 'geruest'). */
    art: EbenenArt;
    ebene: Ebene | null;
    zwischen: [Ebene, Ebene] | null;
    hoeheM: number;
    abstandM: number;
    unsicherheitM: number;
    sicherheit: number;
    nullpunktAlterMin: number;
    /** Nächste Ebene der ANDEREN Liste (Gerüst ↔ Geschoss), wenn konfiguriert und ≤ 1,5 m entfernt. */
    alternative: Ebene | null;
}

/** Nächste Ebene zur Höhe innerhalb von maxM; bei Gleichstand die untere. */
function naechsteEbene(liste: readonly Ebene[], hoeheM: number, maxM: number): Ebene | null {
    let beste: Ebene | null = null;
    let besterAbstand = Number.POSITIVE_INFINITY;
    for (const e of liste) {
        const d = Math.abs(e.hoeheM - hoeheM);
        if (d < besterAbstand) {
            beste = e;
            besterAbstand = d;
        }
    }
    return beste && besterAbstand <= maxM ? beste : null;
}

/** Ergebnis von geschossSchaetzen auf die eigenen Ebenen zurückführen (gleiches Objekt, sonst per id). */
function zurueck(liste: readonly Ebene[], g: { id: string } | null): Ebene | null {
    if (!g) return null;
    return liste.find((e) => e === g) ?? liste.find((e) => e.id === g.id) ?? null;
}

/**
 * Ebene aus dem Luftdruck schätzen. Liste: Gerüstlagen, wenn
 * `sitzung.geruestModus` und ein gültiges Gerüst eingetragen ist; sonst die
 * Geschosse; gibt es keine Geschosse, aber ein Gerüst, die Lagen.
 * `null` ohne Nullpunkt, ohne Ebenen oder bei ungültigem Druck (Höhe nicht
 * berechenbar). Hysterese über `sitzung.vorherigesId` (geschossSchaetzen).
 */
export function ebeneSchaetzen(
    geschosse: readonly VerortungGeschoss[],
    geruest: VerortungGeruest | null | undefined,
    sitzung: VerortungsSitzung,
    druckHpa: number,
    jetzt: number,
): EbenenSchaetzung | null {
    const nullpunkt = sitzung?.nullpunkt;
    if (!nullpunkt) return null;

    const geschossListe = geschossEbenen(geschosse);
    const lagenListe = geruestLagen(geruest);

    let art: EbenenArt;
    if (sitzung.geruestModus && lagenListe.length > 0) art = 'geruest';
    else if (geschossListe.length > 0) art = 'geschoss';
    else if (lagenListe.length > 0) art = 'geruest';
    else return null;

    const primaer = art === 'geruest' ? lagenListe : geschossListe;
    const andere = art === 'geruest' ? geschossListe : lagenListe;

    const s = geschossSchaetzen(primaer, nullpunkt, druckHpa, jetzt, { vorherigesId: sitzung.vorherigesId });
    if (!Number.isFinite(s.hoeheM)) return null;

    const ebene = zurueck(primaer, s.geschoss);
    const unten = s.zwischen ? zurueck(primaer, s.zwischen[0]) : null;
    const oben = s.zwischen ? zurueck(primaer, s.zwischen[1]) : null;

    return {
        art,
        ebene,
        zwischen: unten && oben ? [unten, oben] : null,
        hoeheM: s.hoeheM,
        abstandM: s.abstandM,
        unsicherheitM: s.unsicherheitM,
        sicherheit: s.sicherheit,
        nullpunktAlterMin: s.nullpunktAlterMin,
        alternative: naechsteEbene(andere, s.hoeheM, ALTERNATIVE_MAX_M),
    };
}
